import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CircleCommentResponse, CircleEventResponse } from "@consistency-circle/shared";
import { CIRCLE_COMMENT_MAX_LENGTH } from "@consistency-circle/shared";
import { BellIcon, SendIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiClient } from "../../../lib/api";
import type { ActivityItem } from "../types";
import { CommentRow } from "./CommentRow";
import { EventRow } from "./EventRow";

interface ActivityFeedProps {
  readonly circleId: string;
  readonly comments: CircleCommentResponse[];
  readonly events: CircleEventResponse[];
}

export function ActivityFeed({ circleId, comments, events }: ActivityFeedProps) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  const postComment = useMutation({
    mutationFn: (text: string) => apiClient.createCircleComment(circleId, { message: text }),
    onSuccess: () => {
      setMessage("");
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["circle-comments", circleId] });
    },
    onError: (mutationError) => setError(mutationError.message),
  });

  const items: ActivityItem[] = [
    ...comments.map(
      (comment): ActivityItem => ({
        kind: "comment",
        id: `c-${comment.id}`,
        createdAt: comment.createdAt,
        comment,
      }),
    ),
    ...events.map(
      (event): ActivityItem => ({
        kind: "event",
        id: `e-${event.id}`,
        createdAt: event.createdAt,
        event,
      }),
    ),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const submit = () => {
    const text = message.trim();
    if (text) postComment.mutate(text);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          value={message}
          maxLength={CIRCLE_COMMENT_MAX_LENGTH}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") submit();
          }}
          placeholder="Leave a supportive note..."
        />
        <Button
          size="sm"
          onClick={submit}
          disabled={!message.trim() || postComment.isPending}
          aria-label="Post comment"
        >
          <SendIcon className="w-4 h-4" />
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center">
          <BellIcon className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No activity yet. Say something encouraging.</p>
        </div>
      ) : (
        items.map((item) =>
          item.kind === "comment" ? (
            <CommentRow key={item.id} comment={item.comment} />
          ) : (
            <EventRow key={item.id} event={item.event} />
          ),
        )
      )}
    </div>
  );
}
